// BrandsImporter.js
import React, { useState } from 'react';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import { firebaseInstances } from '../utils/firebaseHelper';
import styles from './BrandsImporter.module.css';

const BrandsImporter = () => {
const [isUploading, setIsUploading] = useState(false);
const [uploadStatus, setUploadStatus] = useState('');
const [statusClass, setStatusClass] = useState('');
const [fileContent, setFileContent] = useState(null);
const [processedCount, setProcessedCount] = useState(0);
const [totalCount, setTotalCount] = useState(0);
const [documentId, setDocumentId] = useState('brands_chunk1');
const { firestore: db } = firebaseInstances;
const storage = getStorage();

const handleFileChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const content = JSON.parse(e.target.result);
      setFileContent(content);
      setUploadStatus(`File loaded successfully. Found ${content.length} brands. Ready to import.`);
      setStatusClass(styles.success);
    } catch (error) {
      setUploadStatus('Error parsing JSON file: ' + error.message);
      setStatusClass(styles.error);
    }
  };
  reader.readAsText(file);
};

// Function to attempt to locate an already uploaded image in Firebase Storage
const getExistingStorageUrl = async (brandName, imageType) => {
  try {
    // Generate the same safe filename pattern that was likely used for chunk1
    const safeBrandName = brandName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    
    // Create a reference to check if the file exists
    // We can't check directly if a file exists in Firebase Storage from client-side
    // But we can try to get the download URL and catch any errors
    
    // Try a few possible timestamp patterns - since we don't know exact timestamps used before
    const possibleFilenames = [
      `${safeBrandName}_${imageType}`, // Try without timestamp
      `${safeBrandName}_${imageType}_` // Will try with wildcard in listAll
    ];
    
    // List all items in the folder that start with our pattern
    const folderRef = ref(storage, 'brands');
    const result = await listAll(folderRef);
    
    // Find files that match our pattern
    for (const item of result.items) {
      const filename = item.name;
      if (possibleFilenames.some(pattern => filename.startsWith(pattern))) {
        // Found a matching file, get its download URL
        return await getDownloadURL(item);
      }
    }
    
    // If we get here, no matching file was found
    return null;
  } catch (error) {
    console.error(`Error checking for existing file for ${brandName}_${imageType}:`, error);
    return null;
  }
};

// Directly reconstruct Firebase Storage URLs
const reconstructChunk2FromChunk1 = async () => {
  if (!fileContent) {
    setUploadStatus('Please select a valid JSON file first.');
    setStatusClass(styles.error);
    return;
  }

  if (documentId !== 'brands_chunk2') {
    setUploadStatus('This operation is only for brands_chunk2. Please select brands_chunk2 as the document ID.');
    setStatusClass(styles.error);
    return;
  }

  setIsUploading(true);
  setUploadStatus('Linking all brand records to existing images in Firebase Storage...');
  setStatusClass(styles.progress);
  setProcessedCount(0);
  setTotalCount(fileContent.length);

  try {
    const updatedBrands = [...fileContent];
    for (let i = 0; i < updatedBrands.length; i++) {
      const brand = updatedBrands[i];
      setProcessedCount(i + 1);
      
      // Try to find existing image URLs for this brand
      if (brand.favicon && brand.favicon.url) {
        const existingUrl = await getExistingStorageUrl(brand.brandName, 'favicon');
        if (existingUrl) {
          brand.favicon.url = existingUrl;
        }
      }
      
      if (brand.logo) {
        const existingUrl = await getExistingStorageUrl(brand.brandName, 'logo');
        if (existingUrl) {
          brand.logo = existingUrl;
        }
      }
      
      if (brand.metadata?.og_metadata?.image) {
        const existingUrl = await getExistingStorageUrl(brand.brandName, 'og_image');
        if (existingUrl) {
          brand.metadata.og_metadata.image = existingUrl;
        }
      }
    }
    
    // Upload to Firestore
    setUploadStatus('Uploading linked data to Firestore...');
    const docRef = doc(db, 'musicalBrands', documentId);
    await setDoc(docRef, { 
      brandsDetails: updatedBrands,
      count: updatedBrands.length,
      updatedAt: new Date()
    });
    
    setUploadStatus(`Successfully linked and uploaded ${updatedBrands.length} brands to Firestore document "${documentId}"!`);
    setStatusClass(styles.success);
  } catch (error) {
    setUploadStatus('Error during linking process: ' + error.message);
    setStatusClass(styles.error);
    console.error('Error:', error);
  } finally {
    setIsUploading(false);
  }
};  // Function to download an image and return it as a Blob
const downloadImage = async (url) => {
  try {
    if (!url) return null;
    
    const response = await fetch(url);
    if (!response.ok) {
      console.warn(`Failed to fetch image: ${url}, status: ${response.status}`);
      return null;
    }
    return await response.blob();
  } catch (error) {
    console.error(`Error downloading image from ${url}:`, error);
    return null;
  }
};  // Handle document-only import (skip image processing)
const handleDocumentOnlyImport = async () => {
  if (!fileContent) {
    setUploadStatus('Please select a valid JSON file first.');
    setStatusClass(styles.error);
    return;
  }

  setIsUploading(true);
  setUploadStatus(`Uploading data directly to Firestore document "${documentId}" (skipping image processing)...`);
  setStatusClass(styles.progress);

  try {
    // Upload to Firestore directly without processing images
    const docRef = doc(db, 'musicalBrands', documentId);
    await setDoc(docRef, { 
      brandsDetails: fileContent,  // Use the original format from your structure
      count: fileContent.length,
      updatedAt: new Date()
    });
    
    setUploadStatus(`Successfully uploaded ${fileContent.length} brands to Firestore document "${documentId}"!`);
    setStatusClass(styles.success);
  } catch (error) {
    setUploadStatus('Error during document import: ' + error.message);
    setStatusClass(styles.error);
    console.error('Import error:', error);
  } finally {
    setIsUploading(false);
  }
};

// Function to upload an image to Firebase Storage
const uploadImageToStorage = async (imageBlob, brandName, imageType) => {
  if (!imageBlob) return null;

  // Generate a safe filename
  const safeBrandName = brandName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  const filename = `${safeBrandName}_${imageType}_${Date.now()}`;
  const imageRef = ref(storage, `brands/${filename}`);

  try {
    await uploadBytes(imageRef, imageBlob);
    const downloadURL = await getDownloadURL(imageRef);
    return downloadURL;
  } catch (error) {
    console.error(`Error uploading ${imageType} for ${brandName}:`, error);
    return null;
  }
};

// Process a single brand's images
const processBrandImages = async (brand) => {
  const updatedBrand = { ...brand };

  // Process favicon if available
  if (brand.favicon && brand.favicon.url) {
    // Try to find already uploaded favicon image
    const existingFaviconUrl = await getExistingStorageUrl(brand.brandName, 'favicon');
    if (existingFaviconUrl) {
      console.log(`Found existing favicon for ${brand.brandName} in Firebase Storage`);
      updatedBrand.favicon.url = existingFaviconUrl;
    } else {
      // Upload new if not found
      const faviconBlob = await downloadImage(brand.favicon.url);
      if (faviconBlob) {
        const newFaviconUrl = await uploadImageToStorage(faviconBlob, brand.brandName, 'favicon');
        if (newFaviconUrl) {
          updatedBrand.favicon.url = newFaviconUrl;
        }
      }
    }
  }

  // Process logo if available
  if (brand.logo) {
    // Try to find already uploaded logo image
    const existingLogoUrl = await getExistingStorageUrl(brand.brandName, 'logo');
    if (existingLogoUrl) {
      console.log(`Found existing logo for ${brand.brandName} in Firebase Storage`);
      updatedBrand.logo = existingLogoUrl;
    } else {
      // Upload new if not found
      const logoBlob = await downloadImage(brand.logo);
      if (logoBlob) {
        const newLogoUrl = await uploadImageToStorage(logoBlob, brand.brandName, 'logo');
        if (newLogoUrl) {
          updatedBrand.logo = newLogoUrl;
        }
      }
    }
  }

  // Process og_metadata image if available
  if (brand.metadata && brand.metadata.og_metadata && brand.metadata.og_metadata.image) {
    // Try to find already uploaded og_image
    const existingOgImageUrl = await getExistingStorageUrl(brand.brandName, 'og_image');
    if (existingOgImageUrl) {
      console.log(`Found existing og_image for ${brand.brandName} in Firebase Storage`);
      updatedBrand.metadata.og_metadata.image = existingOgImageUrl;
    } else {
      // Upload new if not found
      const ogImageBlob = await downloadImage(brand.metadata.og_metadata.image);
      if (ogImageBlob) {
        const newOgImageUrl = await uploadImageToStorage(ogImageBlob, brand.brandName, 'og_image');
        if (newOgImageUrl) {
          updatedBrand.metadata.og_metadata.image = newOgImageUrl;
        }
      }
    }
  }

  setProcessedCount(prev => prev + 1);
  return updatedBrand;
};

const handleImport = async () => {
  if (!fileContent) {
    setUploadStatus('Please select a valid JSON file first.');
    setStatusClass(styles.error);
    return;
  }

  setIsUploading(true);
  setUploadStatus('Processing brands and uploading images to Firebase Storage...');
  setStatusClass(styles.progress);
  setProcessedCount(0);
  setTotalCount(fileContent.length);

  try {
    // Process all brands
    const processedBrands = [];
    
    for (const brand of fileContent) {
      const processedBrand = await processBrandImages(brand);
      processedBrands.push(processedBrand);
    }
    
    // Upload to Firestore
    setUploadStatus(`Uploading processed data to Firestore document "${documentId}"...`);
    
    // Upload to specified document
    const docRef = doc(db, 'musicalBrands', documentId);
    await setDoc(docRef, { 
      brandsDetails: processedBrands,
      count: processedBrands.length,
      updatedAt: new Date()
    });
    
    setUploadStatus(`Successfully processed ${processedBrands.length} brands and uploaded to Firestore document "${documentId}"!`);
    setStatusClass(styles.success);
  } catch (error) {
    setUploadStatus('Error during import: ' + error.message);
    setStatusClass(styles.error);
    console.error('Import error:', error);
  } finally {
    setIsUploading(false);
  }
};

const progressPercentage = totalCount > 0 ? Math.round((processedCount / totalCount) * 100) : 0;

return (
  <div className={styles.container}>
    <h1>Music Brands Importer</h1>
    <p className={styles.description}>
      Upload a JSON file containing music brands to import into the Firestore database.
      Images will be downloaded and stored in Firebase Storage.
    </p>
    
    <div className={styles.uploadContainer}>
      <label className={styles.fileLabel}>
        <span>Select Brands JSON File</span>
        <input 
          type="file" 
          accept=".json" 
          onChange={handleFileChange}
          disabled={isUploading}
        />
      </label>
      
      <div className={styles.docIdSelector}>
        <span>Select Document ID:</span>
        <div className={styles.radioGroup}>
          <label>
            <input
              type="radio"
              name="documentId"
              value="brands_chunk1"
              checked={documentId === 'brands_chunk1'}
              onChange={() => setDocumentId('brands_chunk1')}
              disabled={isUploading}
            />
            brands_chunk1
          </label>
          <label>
            <input
              type="radio"
              name="documentId"
              value="brands_chunk2"
              checked={documentId === 'brands_chunk2'}
              onChange={() => setDocumentId('brands_chunk2')}
              disabled={isUploading}
            />
            brands_chunk2
          </label>
        </div>
      </div>
      
      <div className={styles.importOptions}>
        <button 
          className={styles.importButton} 
          onClick={handleImport}
          disabled={isUploading || !fileContent}
        >
          {isUploading ? 'Processing...' : 'Import Brands and Images'}
        </button>
        
        <button 
          className={`${styles.importButton} ${styles.documentOnlyButton}`} 
          onClick={handleDocumentOnlyImport}
          disabled={isUploading || !fileContent}
        >
          Import Document Only (Skip Image Processing)
        </button>
        
        <button 
          className={`${styles.importButton} ${styles.linkButton}`} 
          onClick={reconstructChunk2FromChunk1}
          disabled={isUploading || !fileContent || documentId !== 'brands_chunk2'}
        >
          Link to Existing Images (Chunk2)
        </button>
      </div>
    </div>
    
    {uploadStatus && (
      <div className={`${styles.status} ${statusClass}`}>
        {uploadStatus}
        {isUploading && totalCount > 0 && (
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${progressPercentage}%` }}
            ></div>
            <span>{processedCount} of {totalCount} ({progressPercentage}%)</span>
          </div>
        )}
      </div>
    )}
    
    {fileContent && !isUploading && (
      <div className={styles.preview}>
        <h3>Preview:</h3>
        <p>{fileContent.length} brands found in file</p>
        <div className={styles.brandsPreview}>
          {fileContent.slice(0, 5).map((brand, index) => (
            <div key={index} className={styles.brandItem}>
              <strong>{brand.brandName}</strong>
              {brand.website && <span>: {brand.website}</span>}
            </div>
          ))}
          {fileContent.length > 5 && <p>...and {fileContent.length - 5} more</p>}
        </div>
      </div>
    )}
  </div>
);
};

export default BrandsImporter;